import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@cloudgenie/ui';
import { API_BASE, useAuth } from '../AuthContext';
import { AdminChrome } from './AdminPage';

/** Wave 5 Phase 6 — CLI device-flow authorize page.
 *  The `cg login` command opens this URL with `?user_code=XXXX-YYYY`.
 *  User confirms; backend binds the code to their session and provisions
 *  an API key that the CLI can now poll for. */

export default function CliAuthorizePage() {
  const { user, loading: authLoading } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const initialCode = (params.get('user_code') || '').trim();
  const [userCode, setUserCode] = useState(initialCode);
  const [status, setStatus] = useState<'idle' | 'ok' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      navigate(`/login?next=${next}`, { replace: true });
    }
  }, [authLoading, user, navigate]);

  async function approve() {
    if (!userCode.trim()) return;
    setBusy(true);
    setStatus('idle');
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}/api/auth/device/approve`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_code: userCode.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setStatus('error');
        setMessage(typeof data.detail === 'string' ? data.detail : `HTTP ${res.status}`);
        return;
      }
      setStatus('ok');
      setMessage(`Authorized as ${data.user_email}. Return to your terminal — the CLI will pick up the credential automatically.`);
    } catch (e) {
      setStatus('error');
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (authLoading || !user) {
    return <AdminChrome title="CLI authorize"><p className="text-center py-16 text-muted-foreground">Checking your session…</p></AdminChrome>;
  }

  return (
    <AdminChrome title="CLI authorize">
      <section className="max-w-lg mx-auto px-6 pt-16 pb-8" data-testid="cli-authorize-page">
        <p className="text-xs uppercase tracking-[0.3em] text-primary mb-2">CLI</p>
        <h1 className="text-3xl font-semibold">Authorize a device</h1>
        <p className="text-muted-foreground mt-2">
          Confirm the code displayed by <code>cg login</code> matches what your terminal shows.
        </p>

        <div className="mt-8 rounded-xl border border-border bg-card p-6">
          <label htmlFor="user_code" className="block text-sm font-medium mb-2">Device code</label>
          <input
            id="user_code"
            data-testid="cli-user-code-input"
            value={userCode}
            onChange={(e) => setUserCode(e.target.value.toUpperCase())}
            placeholder="ABCD-1234"
            className="w-full rounded-md border border-border bg-background px-4 py-3 text-2xl font-mono tracking-widest text-center"
            autoFocus
          />

          <div className="mt-6 flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              Signed in as <strong>{user.email}</strong>.
            </p>
            <Button
              variant="primary"
              onClick={approve}
              disabled={busy || !userCode.trim() || status === 'ok'}
              data-testid="cli-authorize-submit"
            >
              {busy ? 'Authorizing…' : status === 'ok' ? 'Authorized ✓' : 'Authorize'}
            </Button>
          </div>

          {status === 'ok' && (
            <p data-testid="cli-authorize-success" className="mt-4 rounded-lg border border-emerald-700/50 bg-emerald-900/20 text-emerald-200 px-3 py-2 text-sm">
              {message}
            </p>
          )}
          {status === 'error' && (
            <p role="alert" data-testid="cli-authorize-error" className="mt-4 rounded-lg border border-red-700/50 bg-red-900/20 text-red-200 px-3 py-2 text-sm">
              {message}
            </p>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          <Link to="/" className="hover:text-foreground underline">← Back to account</Link>
        </p>
      </section>
    </AdminChrome>
  );
}
